import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePurchaseOrderReport } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subMonths,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function formatNumber(value: number): string {
  return Math.round(value).toLocaleString();
}

function toDateKey(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

type Metric = "count" | "value";

// Payment timing → matrix columns (every PO is in exactly one).
const TIMING_COLUMNS: { key: string; label: string }[] = [
  { key: "pay_in_advance", label: "Paid" },
  { key: "pay_once_booked", label: "Paid once booked" },
  { key: "pay_after", label: "Paid after" },
];

// Known sources in display order; any other value (e.g. "Unknown") is appended.
const SOURCE_ORDER = ["CRM", "Broker", "Friend", "Other"];

export default function PurchaseOrdersReportPage() {
  const now = useMemo(() => new Date(), []);
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(now),
    to: endOfMonth(now),
  });
  const [metric, setMetric] = useState<Metric>("count");

  const startDate = range?.from ? toDateKey(range.from) : "";
  const endDate = toDateKey(range?.to ?? range?.from ?? now);

  const { data, isLoading, isError } = usePurchaseOrderReport({
    startDate,
    endDate,
  });

  const rows = useMemo(() => data ?? [], [data]);

  // Ordered source list: known sources first, then any extras present in data.
  const sources = useMemo(() => {
    const present = new Set(rows.map((r) => r.source));
    const known = SOURCE_ORDER.filter((s) => present.has(s));
    const extra = [...present]
      .filter((s) => !SOURCE_ORDER.includes(s))
      .sort();
    const ordered = [...known, ...extra];
    return ordered.length ? ordered : SOURCE_ORDER;
  }, [rows]);

  // Lookup: `${source}|${timing}` → metric value.
  const cellValue = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const key = `${r.source}|${r.paymentTiming ?? ""}`;
      map.set(key, (map.get(key) ?? 0) + (metric === "count" ? r.count : r.value));
    }
    return map;
  }, [rows, metric]);

  const get = (source: string, timing: string) =>
    cellValue.get(`${source}|${timing}`) ?? 0;

  const rowTotal = (source: string) =>
    TIMING_COLUMNS.reduce((sum, c) => sum + get(source, c.key), 0);
  const colTotal = (timing: string) =>
    sources.reduce((sum, s) => sum + get(s, timing), 0);
  const grandTotal = sources.reduce((sum, s) => sum + rowTotal(s), 0);

  const totalCount = rows.reduce((sum, r) => sum + r.count, 0);
  const totalValue = rows.reduce((sum, r) => sum + r.value, 0);

  const fmt = (value: number) =>
    metric === "value" ? currencyFormatter.format(value) : formatNumber(value);

  const setPreset = (preset: "this" | "last" | "last3" | "ytd") => {
    if (preset === "this") {
      setRange({ from: startOfMonth(now), to: endOfMonth(now) });
    } else if (preset === "last") {
      const prev = subMonths(now, 1);
      setRange({ from: startOfMonth(prev), to: endOfMonth(prev) });
    } else if (preset === "last3") {
      setRange({ from: startOfMonth(subMonths(now, 2)), to: endOfMonth(now) });
    } else {
      setRange({ from: startOfYear(now), to: endOfYear(now) });
    }
  };

  const rangeLabel =
    range?.from && range?.to
      ? `${format(range.from, "PP")} – ${format(range.to, "PP")}`
      : range?.from
        ? format(range.from, "PP")
        : "Pick a range";

  return (
    <div className="container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Purchase Orders
          </h1>
          <p className="text-sm text-muted-foreground">
            PO volume for the selected period, broken down by source and payment
            type. Voided POs are excluded; value is the gross PO amount (buy
            price + tax).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setPreset("this")}>
              This month
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("last")}>
              Last month
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPreset("last3")}
            >
              Last 3 months
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPreset("ytd")}>
              YTD
            </Button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[260px] justify-start text-left font-normal",
                  !range?.from && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={2}
                defaultMonth={range?.from}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Headline + metric toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="grid flex-1 gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Purchase Orders</CardDescription>
              <CardTitle className="text-3xl">
                {formatNumber(totalCount)}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Total POs raised in range (excl. voided)
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Value</CardDescription>
              <CardTitle className="text-3xl">
                {currencyFormatter.format(totalValue)}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              Gross PO amount (buy price + tax)
            </CardContent>
          </Card>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant={metric === "count" ? "default" : "outline"}
            onClick={() => setMetric("count")}
          >
            Count
          </Button>
          <Button
            size="sm"
            variant={metric === "value" ? "default" : "outline"}
            onClick={() => setMetric("value")}
          >
            Value
          </Button>
        </div>
      </div>

      {/* Source × payment-type matrix */}
      <Card>
        <CardHeader>
          <CardTitle>
            By Source &amp; Payment Type ({metric === "value" ? "value" : "count"})
          </CardTitle>
          <CardDescription>
            Rows are acquisition source, columns are payment type. Cells show{" "}
            {metric === "value" ? "total PO value" : "number of POs"}; totals on
            the right and bottom.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Loading purchase order report…
            </p>
          ) : isError ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Couldn't load the report. Try again or adjust the range.
            </p>
          ) : (
            <Table containerClassName="overflow-auto">
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow>
                  <TableHead>Source</TableHead>
                  {TIMING_COLUMNS.map((c) => (
                    <TableHead key={c.key} className="text-right">
                      {c.label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right font-semibold">
                    Total
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source}>
                    <TableCell className="font-medium">{source}</TableCell>
                    {TIMING_COLUMNS.map((c) => (
                      <TableCell key={c.key} className="text-right">
                        {fmt(get(source, c.key))}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-medium">
                      {fmt(rowTotal(source))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell className="font-semibold">Total</TableCell>
                  {TIMING_COLUMNS.map((c) => (
                    <TableCell
                      key={c.key}
                      className="text-right font-semibold"
                    >
                      {fmt(colTotal(c.key))}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-semibold">
                    {fmt(grandTotal)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
